import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/controls/customControls/dialog";
import { Button } from "@/components/controls/customControls/button";
import { Input } from "@/components/controls/customControls/input";
import { Label } from "@/components/controls/customControls/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/controls/customControls/select";
import {
  RadioGroup,
  RadioGroupItem,
} from "@/components/controls/customControls/radio-group";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { usePatientBookingData } from "./patientBookingData";
import { getDefaultCompanyServices } from "@/dispatchers/pharmacyServiceDispatchers";
import {
  setIncrementBookingCompleted,
  setBookingDetails,
  updateConsultationActions,
  trackServiceBilling,
  setAddPatientAIChatMessage,
  setUpdateAppointmentAIChatMessage,
  SaveAIChatMessage,
} from "@/dispatchers/patientDispatchers";
import {
  addScheduleEvent,
  getUsrAvailibility,
} from "@/dispatchers/scheduleEventsDispatchers";
import { parse, addMinutes, format } from "date-fns";
import { v4 as uuidv4 } from "uuid";
import { ActionType } from "@/enum/ActionType";
import useTitanApp from "@/hooks/useTitanApp";
import removeActionFromLocal from "@/utils/removeFromLocalStorage.js";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import {
  Calendar,
  Bell,
  CheckSquare,
  Trash2,
  Plus,
  Syringe,
} from "lucide-react";

interface AppointmentAction {
  id: string;
  label: string;
  completed?: boolean;
}

interface AppointmentVaccine {
  id: string;
  name: string;
  vaccineName?: string;
  dose?: string;
  notes?: string;
  completed?: boolean;
  prescriptionItemId?: string;
  manufacturer?: string;
}

type PatientBookingForm = {
  patientName: string;
  patientId?: string;
  pharmacist: string;
  serviceType: string;
  date: string;
  time: string;
  duration: string;
  room?: string;
  notes?: string;
  email?: string;
  mobile?: string;
  confirmationMethod?: "email" | "mobile";
};

interface PatientBookingModalProps {
  show: boolean;
  handleClose: () => void;
  patientName: string;
  patientId?: string;
  isDarkMode?: boolean;
  email?: string;
  mobile?: string;
  initialDate?: string;
  initialTime?: string;
  initialServiceTypeId?: string;
  initialPharmacistName?: string;
  shouldRefreshAppointments?: boolean;
  onRefreshAppointments?: (patientId: string) => Promise<void>;
}

const PatientBookingModal: React.FC<PatientBookingModalProps> = ({
  show,
  handleClose,
  patientName,
  patientId,
  isDarkMode = false,
  email,
  mobile,
  initialDate,
  initialTime,
  initialServiceTypeId,
  initialPharmacistName,
  shouldRefreshAppointments,
  onRefreshAppointments,
}) => {
  const { patientState, scheduleEventState } = useTitanApp();
  const { bookingDetails, selectedPatientConsultation } = patientState || {};
  const { bookingSlotsMode } = scheduleEventState || {};
  const {
    servicePharmacists = [],
    serviceTypesInfo = [],
    isEventCalendarLoading = false,
    onServiceTypeSelected,
    serviceRooms = [],
    serviceDurations = [],
    companyId = "",
    currentPharmacyId = "",
    currentConsultationId = "",
    availableDates = [],
    getAvailableTimesForDate,
    handleGetAppointmentRooms,
  } = usePatientBookingData();
  const isConsultationCompleted =
    selectedPatientConsultation?.consultationStatus === 1;
  const bookingSchema = React.useMemo(() => {
    return z
      .object({
        patientName: z.string().min(1, "Patient name is required"),
        patientId: z.string().optional(),
        pharmacist: z.string().min(1, "Pharmacist is required"),
        serviceType: z.string().min(1, "Service type is required"),
        date: z.string().min(1, "Date is required"),
        time: z.string().min(1, "Time is required"),
        duration: z.string().min(1, "Duration is required"),
        room: z.string().optional(),
        notes: z.string().optional(),
        email: z
          .string()
          .trim()
          .max(254)
          .email("Invalid email address")
          .optional()
          .or(z.literal("")),
        mobile: z
          .string()
          .trim()
          .max(20)
          .regex(/^[0-9+()\-\s]*$/i, "Invalid phone number")
          .optional()
          .or(z.literal("")),
        confirmationMethod: z.enum(["email", "mobile"]).optional(),
      })
      .superRefine((data, ctx) => {
        const svc = (serviceTypesInfo || []).find(
          (s: any) => s?._id === data.serviceType,
        );
        if (svc && (svc as any).isRoomRequired) {
          const v = (data.room || "").trim();
          if (!v) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["room"],
              message: "Room is required for this service",
            });
          }
        }
        if (data.confirmationMethod === "email") {
          const v = (data.email || "").trim();
          if (!v) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["email"],
              message: "Email is required for email confirmation",
            });
          }
        }
        if (data.confirmationMethod === "mobile") {
          const v = (data.mobile || "").trim();
          if (!v) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["mobile"],
              message: "Mobile is required for SMS confirmation",
            });
          }
        }
      });
  }, [serviceTypesInfo]);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    getValues,
    formState: { errors },
  } = useForm<PatientBookingForm>({
    resolver: zodResolver(bookingSchema),
    defaultValues: {
      patientName: patientName || "",
      patientId: patientId || "",
      duration: "",
      date: "",
      time: "",
      email: email || "",
      mobile: mobile || "",
      confirmationMethod: undefined,
    },
  });

  // Task and Vaccine state - declare early so they can be used in dependency arrays
  const [internalTaskLabel, setInternalTaskLabel] = React.useState("");
  const [internalVaccineId, setInternalVaccineId] = React.useState("");
  const [localTasks, setLocalTasks] = React.useState<AppointmentAction[]>([]);
  const [localVaccines, setLocalVaccines] = React.useState<
    AppointmentVaccine[]
  >([]);

  const taskLabel = internalTaskLabel;
  const vaccineIdState = internalVaccineId;

  const handleTaskLabelChange = (val: string) => {
    setInternalTaskLabel(val);
  };

  const handleVaccineIdChange = (val: string) => {
    setInternalVaccineId(val);
  };

  const handleAddTask = (label: string) => {
    if (!label.trim()) return;
    const newTask: AppointmentAction = {
      id: `task-${Date.now()}`,
      label: label.trim(),
      completed: false,
    };
    setLocalTasks((prev) => [...prev, newTask]);
    setInternalTaskLabel("");
  };

  const handleRemoveTask = (id: string) => {
    setLocalTasks((prev) => prev.filter((t) => t.id !== id));
  };

  const handleRemoveVaccine = (id: string) => {
    setLocalVaccines((prev) => prev.filter((v) => v.id !== id));
  };

  const handleAddVaccine = (id: string) => {
    if (!id.trim()) return;
    const newVaccine: AppointmentVaccine = {
      id: `vaccine-${Date.now()}`,
      name: id.trim(),
      completed: false,
    };
    setLocalVaccines((prev) => [...prev, newVaccine]);
    setInternalVaccineId("");
  };

  // Helper to ensure tasks have proper structure
  const normalizeTask = (task: any): AppointmentAction => {
    if (typeof task === "string") {
      return {
        id: `task-${Date.now()}-${Math.random()}`,
        label: task,
        completed: false,
      };
    }
    return {
      id: task.id || `task-${Date.now()}-${Math.random()}`,
      label: task.label || String(task),
      completed: task.completed || false,
    };
  };

  // Helper to ensure vaccines have proper structure
  const normalizeVaccine = (vaccine: any): AppointmentVaccine => {
    if (typeof vaccine === "string") {
      return {
        id: `vaccine-${Date.now()}-${Math.random()}`,
        name: vaccine,
        completed: false,
      };
    }
    return {
      id: vaccine.id || `vaccine-${Date.now()}-${Math.random()}`,
      name: vaccine.name || String(vaccine),
      dose: vaccine.dose || "",
      notes: vaccine.notes || "",
      completed: vaccine.completed || false,
    };
  };

  // Add helper function
  const resetDefaultValues = React.useCallback(() => {
    // Match serviceType ID from initialServiceTypeId or serviceTypesInfo
    const matchedService = (serviceTypesInfo || []).find(
      (s: any) =>
        s?._id === initialServiceTypeId ||
        s?.name === initialServiceTypeId ||
        (s?.name &&
          initialServiceTypeId &&
          s.name.trim().toLowerCase() ===
            initialServiceTypeId.trim().toLowerCase()) ||
        String(s?.serviceType) === String(initialServiceTypeId),
    );
    const serviceVal = matchedService?._id || initialServiceTypeId || undefined;

    // Match pharmacist ID from initialPharmacistName or servicePharmacists
    const matchedPharmacist = (servicePharmacists || []).find(
      (p: any) =>
        p?.id === initialPharmacistName ||
        (p?.name &&
          initialPharmacistName &&
          p.name.trim().toLowerCase() ===
            initialPharmacistName.trim().toLowerCase()),
    );
    const pharmacistVal = matchedPharmacist?.id || initialPharmacistName || "";

    reset({
      patientName: patientName || "",
      patientId: patientId || "",
      duration: "30",
      date: initialDate || "",
      time: initialTime || "",
      pharmacist: pharmacistVal,
      serviceType: serviceVal as any,
      room: "",
      notes: "",
      email: email || "",
      mobile: mobile || "",
      confirmationMethod: undefined,
    });
    setLocalTasks([]);
    setLocalVaccines([]);
    setInternalTaskLabel("");
    setInternalVaccineId("");
  }, [
    reset,
    patientName,
    patientId,
    email,
    mobile,
    initialDate,
    initialTime,
    initialServiceTypeId,
    initialPharmacistName,
    serviceTypesInfo,
    servicePharmacists,
  ]);

  React.useEffect(() => {
    if (show) {
      if (bookingDetails) {
        try {
          const details =
            typeof bookingDetails === "string"
              ? JSON.parse(bookingDetails)
              : bookingDetails.payload
                ? JSON.parse(bookingDetails.payload)
                : bookingDetails;

          let appointmentDate = "";
          let appointmentTime = "";

          if (details.startDate) {
            const startDate = new Date(details.startDate);
            appointmentDate = format(startDate, "yyyy-MM-dd");
            appointmentTime = format(startDate, "HH:mm");
          }

          reset({
            patientName: patientName || "",
            patientId: details.patientID || patientId || "",
            pharmacist: details.resourceId || "",
            serviceType: details.serviceID || "",
            date: appointmentDate,
            time: appointmentTime,
            duration: String(details.duration || "30"),
            room: details.RoomId || "",
            notes: details.name || "",
            email: details.email || email || "",
            mobile: details.mobile || mobile || "",
            confirmationMethod: details.confirmationMethod || undefined,
          });

          // Load tasks and vaccines from booking details
          if (details.tasksToComplete?.length > 0) {
            const normalizedTasks = details.tasksToComplete.map(normalizeTask);
            setLocalTasks(normalizedTasks);
          } else {
            setLocalTasks([]);
          }

          if (details.vaccinesAdminister?.length > 0) {
            const normalizedVaccines =
              details.vaccinesAdminister.map(normalizeVaccine);
            setLocalVaccines(normalizedVaccines);
          } else {
            setLocalVaccines([]);
          }
        } catch (error) {
          console.error("Error parsing booking details:", error);
          resetDefaultValues();
          setLocalTasks([]);
          setLocalVaccines([]);
        }
      } else {
        resetDefaultValues();
        setLocalTasks([]);
        setLocalVaccines([]);
      }
    }
  }, [
    show,
    patientName,
    patientId,
    email,
    mobile,
    reset,
    bookingDetails,
    resetDefaultValues,
  ]);

  // Fetch company services when dialog opens
  React.useEffect(() => {
    if (show && companyId) {
      getDefaultCompanyServices(companyId);
    }
  }, [show, companyId]);

  React.useEffect(() => {
    if (show) {
      const svc = watch("serviceType");
      if (svc) {
        onServiceTypeSelected?.(svc);
        const pharm = watch("pharmacist");
        if (pharm) {
          getUsrAvailibility(pharm, svc, currentPharmacyId);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, initialServiceTypeId, initialPharmacistName]);

  const selectedDate = watch("date") || "";
  const selectedTime = watch("time") || "";
  const availableTimes = React.useMemo(() => {
    const times = getAvailableTimesForDate
      ? getAvailableTimesForDate(selectedDate)
      : [];

    // If we're in edit mode and have a booking time, include it even if not in available slots
    if (bookingDetails && selectedTime && !times.includes(selectedTime)) {
      return [...times, selectedTime].sort();
    }

    return times;
  }, [getAvailableTimesForDate, selectedDate, bookingDetails, selectedTime]);

  React.useEffect(() => {
    if (selectedDate && !availableDates?.includes(selectedDate)) {
      setValue("date", "");
      setValue("time", "");
    }
  }, [availableDates]);
  React.useEffect(() => {
    if (
      selectedTime &&
      availableTimes &&
      !availableTimes.includes(selectedTime)
    ) {
      setValue("time", "");
    }
  }, [availableTimes]);

  const onSubmit = React.useCallback(
    async (data: PatientBookingForm) => {
      if (isConsultationCompleted) return;

      let existingId = null;
      let existingConsultationId = null;

      if (bookingDetails) {
        try {
          let parsedDetails;
          if (typeof bookingDetails === "string") {
            parsedDetails = JSON.parse(bookingDetails);
          } else if (bookingDetails.payload) {
            parsedDetails = JSON.parse(bookingDetails.payload);
          } else {
            parsedDetails = bookingDetails;
          }

          existingId = parsedDetails.id;
          existingConsultationId = parsedDetails.consultationID;
        } catch (e) {
          console.error("❌ Failed to parse bookingDetails:", e);
        }
      }

      const gmtDate = parse(
        `${data?.date} ${data?.time}`,
        "yyyy-MM-dd HH:mm",
        new Date(),
      );

      const startDateTime = new Date(
        gmtDate.getTime() - gmtDate.getTimezoneOffset() * 60000,
      );
      const endDateTime = addMinutes(startDateTime, parseInt(data?.duration));

      let appointmentId;
      let consultationId;

      if (existingConsultationId) {
        appointmentId = existingId;
        consultationId = existingConsultationId;
      } else {
        const newId = uuidv4();
        appointmentId = newId;
        consultationId = newId;
      }

      const appointment = {
        id: appointmentId,
        consultationID: consultationId,
        name: data?.notes || "",
        patientID: data?.patientId,
        // startDate: format(startDateTime, "yyyy-MM-dd'T'HH:mm:ss'Z'"),
        // endDate: format(endDateTime, "yyyy-MM-dd'T'HH:mm:ss'Z'"),
        startDate: startDateTime.toISOString(),
        endDate: endDateTime.toISOString(),
        duration: parseInt(data?.duration || "0", 10) || 0,
        durationUnit: "m",
        cls: "",
        recurrenceRule: "",
        exceptionDates: [] as string[],
        resourceId: data?.pharmacist,
        allDay: false,
        scheduleEventType: 1,
        serviceID: data?.serviceType,
        email: data?.email || "",
        mobile: data?.mobile || "",
        confirmationMethod: data?.confirmationMethod || "",
        RoomId: data?.room || "",
        recordedAt: new Date().toLocaleString(),
        tasksToComplete: localTasks,
        vaccinesAdminister: localVaccines,
      };

      const appointmentPayload = {
        payload: JSON.stringify(appointment),
        actionName: ActionType[4],
        actionNumber: "4",
        isPerformed: true,
      };

      const appointmentMessageId = `appointment-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      await addScheduleEvent(appointment, currentPharmacyId).then(() => {
        if (currentConsultationId !== "") {
          updateConsultationActions({
            consultationId: currentConsultationId,
            payload: appointmentPayload,
          });

          const consultationChatPayload = {
            ConsultationChat: JSON.stringify([
              {
                sender: "success",
                message: "Appointment booked successfully.",
                actionId: 4,
                details: {
                  payload: JSON.stringify(appointmentPayload),
                  actionName: "BookAppointment",
                  actionNumber: "4",
                  isPerformed: true,
                  performedAt: new Date().toISOString(),
                },
              },
            ]),
            ConsultationId: currentConsultationId,
          };

          SaveAIChatMessage(consultationChatPayload);

          setUpdateAppointmentAIChatMessage({
            sender: "success",
            message: "Booking completed successfully.",
            actionId: 4,
            details: appointmentPayload,
            timestamp: new Date().toISOString(),
            appointmentMessageId: appointmentMessageId,
            ...(currentConsultationId && {
              originalConsultationId: currentConsultationId,
            }),
          });

          setBookingDetails(appointmentPayload);
          removeActionFromLocal(currentConsultationId, "4");
          setIncrementBookingCompleted(true);
        }

        if (currentConsultationId !== "") {
          try {
            trackServiceBilling({
              companyId,
              consultationId: consultationId,
              pharmacyId: currentPharmacyId,
              actiontype: "appointment",
            });
          } catch (err) {
            console.error(
              "Error calling billing tracker for appointment:",
              err,
            );
          }
        }
      });

      if (shouldRefreshAppointments && onRefreshAppointments && patientId) {
        await onRefreshAppointments(patientId);
      }
      handleClose();
      reset();
      setLocalTasks([]);
      setLocalVaccines([]);
    },
    [
      bookingDetails,
      handleClose,
      reset,
      currentConsultationId,
      companyId,
      currentPharmacyId,
      shouldRefreshAppointments,
      onRefreshAppointments,
      patientId,
      isConsultationCompleted,
      localTasks,
      localVaccines,
    ],
  );
  const currentDuration = watch("duration");
  const durations = React.useMemo(() => {
    const list = [...(serviceDurations || [])];
    if (
      currentDuration &&
      !list.some((d) => d.value === String(currentDuration))
    ) {
      list.push({
        value: String(currentDuration),
        label: `${currentDuration} minutes`,
      });
    }
    return list;
  }, [currentDuration, serviceDurations]);

  const todayStr = React.useMemo(() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }, []);
  const [dateError, setDateError] = React.useState<string | null>(null);

  // Determine which accordion items should be open by default
  const defaultAccordionValues = React.useMemo(() => {
    const defaults = ["essential-info", "notifications"];
    if (localTasks?.length > 0) defaults.push("tasks");
    if (localVaccines?.length > 0) defaults.push("vaccines");
    return defaults;
  }, [localTasks?.length, localVaccines?.length]);

  return (
    <Dialog
      open={show}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
    >
      <DialogContent
        className={`sm:max-w-[600px] transition-colors duration-300 max-h-[100vh] overflow-y-auto ${
          isDarkMode
            ? "bg-gray-800 border-gray-600 text-white"
            : "bg-white border-gray-200 text-gray-900"
        }`}
      >
        <DialogHeader className={`flex-shrink-0`}>
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <DialogTitle
                className={`text-lg font-semibold transition-colors duration-300 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                {bookingDetails ? "Edit Appointment" : "New Appointment"}
              </DialogTitle>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <Accordion
            type="multiple"
            defaultValue={defaultAccordionValues}
            className={`w-full border-t transition-colors duration-300 ${
              isDarkMode ? "border-gray-600" : "border-gray-200"
            }`}
          >
            <AccordionItem
              value="essential-info"
              className={`border-b transition-colors duration-300 ${
                isDarkMode ? "border-gray-600" : "border-gray-200"
              }`}
            >
              <AccordionTrigger
                className={`px-0 py-4 ${
                  isDarkMode ? "hover:bg-gray-700/50" : "hover:bg-gray-50"
                }`}
              >
                <div className="flex items-center gap-2 font-semibold">
                  <Calendar className="w-5 h-5" />
                  <span>Essential Information</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pt-0 px-2 pb-6 !overflow-visible">
                <div className="grid grid-cols-2 gap-4">
                  {/* Patient Name (full width) */}
                  <div className="col-span-2 space-y-2">
                    <Label
                      className={`text-sm font-medium transition-colors duration-300 ${
                        isDarkMode ? "text-gray-200" : "text-gray-700"
                      }`}
                    >
                      Patient Name *
                    </Label>
                    <Input
                      value={watch("patientName") || ""}
                      tabIndex={-1}
                      onFocus={(e) => e.target.blur()}
                      className={`${
                        isDarkMode
                          ? "bg-gray-700 border-gray-600 text-white"
                          : "bg-white border-gray-200 text-gray-900"
                      } cursor-default`}
                      style={{ pointerEvents: "none" }}
                      disabled={
                        isEventCalendarLoading ||
                        bookingSlotsMode === "fromAppointment"
                      }
                    />
                    {errors.patientName && (
                      <p
                        className={`text-sm transition-colors duration-300 ${
                          isDarkMode ? "text-red-400" : "text-red-600"
                        }`}
                      >
                        {errors.patientName.message}
                      </p>
                    )}
                  </div>

                  {/* Service Type */}
                  <div className="space-y-2">
                    <Label
                      className={`text-sm font-medium transition-colors duration-300 ${
                        isDarkMode ? "text-gray-200" : "text-gray-700"
                      }`}
                    >
                      Service Type *
                    </Label>
                    <Select
                      value={watch("serviceType")}
                      onValueChange={async (value: string) => {
                        try {
                          await onServiceTypeSelected?.(value);
                          setValue("serviceType", value);

                          const currentPharmacistId = getValues("pharmacist");
                          if (currentPharmacistId) {
                            await getUsrAvailibility(
                              currentPharmacistId,
                              value,
                              currentPharmacyId,
                            );
                          }

                          const formData = getValues();
                          await handleGetAppointmentRooms?.({
                            ...formData,
                            isNewAppointment: !bookingDetails,
                          });
                        } finally {
                          setValue("serviceType", value);
                          const svc = (serviceTypesInfo || []).find(
                            (s: any) => s?._id === value,
                          );
                          const base =
                            parseInt(
                              String((svc as any)?.serviceDuration ?? 0),
                              10,
                            ) || 0;
                          if (base > 0) setValue("duration", String(base));
                        }
                      }}
                      disabled={
                        isEventCalendarLoading || isConsultationCompleted
                      }
                    >
                      <SelectTrigger
                        className="w-full"
                        disabled={
                          isEventCalendarLoading || isConsultationCompleted
                        }
                      >
                        <SelectValue placeholder="Select service" />
                      </SelectTrigger>
                      <SelectContent>
                        {serviceTypesInfo
                          ?.filter(
                            (service) =>
                              service?.serviceType !== 1 &&
                              service?.serviceType !== 3,
                          )
                          .map((service) => (
                            <SelectItem key={service?._id} value={service?._id}>
                              {service?.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    {errors.serviceType && (
                      <p
                        className={`text-sm transition-colors duration-300 ${
                          isDarkMode ? "text-red-400" : "text-red-600"
                        }`}
                      >
                        {errors.serviceType.message}
                      </p>
                    )}
                  </div>

                  {/* Pharmacist */}
                  <div className="space-y-2">
                    <Label
                      className={`text-sm font-medium transition-colors duration-300 ${
                        isDarkMode ? "text-gray-200" : "text-gray-700"
                      }`}
                    >
                      Pharmacist *
                    </Label>
                    <Select
                      value={watch("pharmacist")}
                      onValueChange={async (value) => {
                        try {
                          const svc = watch("serviceType");
                          if (svc) {
                            await getUsrAvailibility(
                              value,
                              svc,
                              currentPharmacyId,
                            );
                          }
                        } finally {
                          setValue("pharmacist", value);
                          const formData = getValues();
                          await handleGetAppointmentRooms({
                            ...formData,
                            isNewAppointment: !bookingDetails,
                          });
                        }
                      }}
                      disabled={
                        isEventCalendarLoading || isConsultationCompleted
                      }
                    >
                      <SelectTrigger
                        className="w-full"
                        disabled={
                          isEventCalendarLoading || isConsultationCompleted
                        }
                      >
                        <SelectValue placeholder="Select pharmacist" />
                      </SelectTrigger>
                      <SelectContent>
                        {servicePharmacists.map((pharmacist) => (
                          <SelectItem
                            key={pharmacist?.id}
                            value={pharmacist?.id}
                          >
                            {pharmacist?.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.pharmacist && (
                      <p
                        className={`text-sm transition-colors duration-300 ${
                          isDarkMode ? "text-red-400" : "text-red-600"
                        }`}
                      >
                        {errors.pharmacist.message}
                      </p>
                    )}
                  </div>

                  {/* Date */}
                  <div className="space-y-2">
                    <Label
                      className={`text-sm font-medium transition-colors duration-300 ${
                        isDarkMode ? "text-gray-200" : "text-gray-700"
                      }`}
                    >
                      Date *
                    </Label>
                    <Input
                      id="date"
                      type="date"
                      value={selectedDate}
                      onChange={async (e) => {
                        const v = (e.target as HTMLInputElement).value;
                        setValue("date", v);
                        const formData = getValues();
                        await handleGetAppointmentRooms({
                          ...formData,
                          isNewAppointment: !bookingDetails,
                        });

                        const today = new Date();
                        const todayStr = `${today.getFullYear()}-${String(
                          today.getMonth() + 1,
                        ).padStart(2, "0")}-${String(today.getDate()).padStart(
                          2,
                          "0",
                        )}`;
                        if (!availableDates?.includes(v) || v < todayStr) {
                          setDateError(
                            "This date has no available slots for the selected pharmacist and service.",
                          );
                          setValue("date", "");
                          setValue("time", "");
                          return;
                        }
                        setDateError(null);
                        setValue("date", v, {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                        const times = getAvailableTimesForDate
                          ? getAvailableTimesForDate(v)
                          : [];
                        if (!times.includes(watch("time") || "")) {
                          setValue("time", "");
                        }
                      }}
                      disabled={
                        isEventCalendarLoading ||
                        !availableDates?.length ||
                        isConsultationCompleted
                      }
                      min={todayStr}
                      className="w-full"
                    />
                    {errors.date && (
                      <p
                        className={`text-sm transition-colors duration-300 ${
                          isDarkMode ? "text-red-400" : "text-red-600"
                        }`}
                      >
                        {errors.date.message}
                      </p>
                    )}
                    {dateError && (
                      <p
                        className={`text-sm mt-1 ${
                          isDarkMode ? "text-red-400" : "text-red-600"
                        }`}
                      >
                        {dateError}
                      </p>
                    )}
                  </div>

                  {/* Time */}
                  <div className="space-y-2">
                    <Label
                      className={`text-sm font-medium transition-colors duration-300 ${
                        isDarkMode ? "text-gray-200" : "text-gray-700"
                      }`}
                    >
                      Time *
                    </Label>
                    <Select
                      value={selectedTime}
                      onValueChange={async (value) => {
                        setValue("time", value);
                        const formData = getValues();
                        await handleGetAppointmentRooms({
                          ...formData,
                          isNewAppointment: !bookingDetails,
                        });
                      }}
                      disabled={
                        isConsultationCompleted ||
                        isEventCalendarLoading ||
                        !selectedDate ||
                        availableTimes.length === 0
                      }
                    >
                      <SelectTrigger
                        className="w-full"
                        disabled={
                          isConsultationCompleted ||
                          isEventCalendarLoading ||
                          !selectedDate ||
                          availableTimes.length === 0
                        }
                      >
                        <SelectValue placeholder="Select time" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableTimes.map((time) => (
                          <SelectItem key={time} value={time}>
                            {time}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.time && (
                      <p
                        className={`text-sm transition-colors duration-300 ${
                          isDarkMode ? "text-red-400" : "text-red-600"
                        }`}
                      >
                        {errors.time.message}
                      </p>
                    )}
                  </div>

                  {/* Duration */}
                  <div className="space-y-2">
                    <Label
                      className={`text-sm font-medium transition-colors duration-300 ${
                        isDarkMode ? "text-gray-200" : "text-gray-700"
                      }`}
                    >
                      Duration *
                    </Label>
                    <Select
                      value={watch("duration")}
                      onValueChange={async (value) => {
                        setValue("duration", value);
                        const formData = getValues();
                        await handleGetAppointmentRooms({
                          ...formData,
                          isNewAppointment: !bookingDetails,
                        });
                      }}
                      disabled={
                        isEventCalendarLoading || isConsultationCompleted
                      }
                    >
                      <SelectTrigger
                        className="w-full"
                        disabled={
                          isEventCalendarLoading || isConsultationCompleted
                        }
                      >
                        <SelectValue placeholder="Select duration" />
                      </SelectTrigger>
                      <SelectContent>
                        {durations.map((duration) => (
                          <SelectItem
                            key={duration.value}
                            value={duration.value}
                          >
                            {duration.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.duration && (
                      <p
                        className={`text-sm transition-colors duration-300 ${
                          isDarkMode ? "text-red-400" : "text-red-600"
                        }`}
                      >
                        {errors.duration.message}
                      </p>
                    )}
                  </div>

                  {/* Room */}
                  <div className="space-y-2">
                    <Label
                      className={`text-sm font-medium transition-colors duration-300 ${
                        isDarkMode ? "text-gray-200" : "text-gray-700"
                      }`}
                    >
                      {(() => {
                        const svc = (serviceTypesInfo || []).find(
                          (s: any) => s?._id === watch("serviceType"),
                        );
                        return svc && (svc as any).isRoomRequired
                          ? "Room *"
                          : "Room";
                      })()}
                    </Label>
                    <Select
                      value={watch("room") || "none"}
                      onValueChange={(value) =>
                        setValue("room", value === "none" ? "" : value)
                      }
                      disabled={
                        isEventCalendarLoading || isConsultationCompleted
                      }
                    >
                      <SelectTrigger
                        className="w-full"
                        disabled={
                          isEventCalendarLoading || isConsultationCompleted
                        }
                      >
                        <SelectValue
                          placeholder={(() => {
                            const svc = (serviceTypesInfo || []).find(
                              (s: any) => s?._id === watch("serviceType"),
                            );
                            return svc && (svc as any).isRoomRequired
                              ? "Select room"
                              : "Select room (optional)";
                          })()}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No room assigned</SelectItem>
                        {serviceRooms.map((room) => (
                          <SelectItem key={room.id} value={room.id}>
                            {room.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.room && (
                      <p
                        className={`text-sm transition-colors duration-300 ${
                          isDarkMode ? "text-red-400" : "text-red-600"
                        }`}
                      >
                        {errors.room.message}
                      </p>
                    )}
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem
              value="notifications"
              className={`border-b transition-colors duration-300 ${
                isDarkMode ? "border-gray-600" : "border-gray-200"
              }`}
            >
              <AccordionTrigger
                className={`px-0 py-4 ${
                  isDarkMode ? "hover:bg-gray-700/50" : "hover:bg-gray-50"
                }`}
              >
                <div className="flex items-center gap-2 font-semibold">
                  <Bell className="w-5 h-5" />
                  <span>Notifications</span>
                  {watch("confirmationMethod") && (
                    <span
                      className={`text-xs font-normal ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      (
                      {watch("confirmationMethod") === "email"
                        ? "Email"
                        : watch("confirmationMethod") === "mobile"
                          ? "SMS"
                          : "Do not send"}
                      )
                    </span>
                  )}
                  {!watch("confirmationMethod") && (
                    <span
                      className={`text-xs font-normal ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      (Do not send)
                    </span>
                  )}
                </div>
              </AccordionTrigger>

              <AccordionContent className="pt-0 px-2 pb-6 !overflow-visible">
                {/* Contact Information */}
                <div className="col-span-2 space-y-2">
                  <Label
                    className={`text-sm font-medium transition-colors duration-300 ${
                      isDarkMode ? "text-gray-200" : "text-gray-700"
                    }`}
                  >
                    Contact Information
                  </Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label
                        className={`text-xs ${
                          isDarkMode ? "text-gray-300" : "text-gray-600"
                        }`}
                      >
                        Email
                      </Label>
                      <Input
                        type="email"
                        value={watch("email") || ""}
                        inputMode="email"
                        autoComplete="email"
                        maxLength={254}
                        placeholder="name@email.com"
                        pattern="^[^\s@]+@[^\s@]+\.[^\s@]+$"
                        disabled={isConsultationCompleted}
                        {...register("email", {
                          onChange: (e) => {
                            const val = (
                              e.target as HTMLInputElement
                            ).value.replace(/\s+/g, "");
                            setValue("email", val, {
                              shouldValidate: true,
                              shouldDirty: true,
                            });
                          },
                          onBlur: (e) => {
                            setValue(
                              "email",
                              (e.target as HTMLInputElement).value.trim(),
                              { shouldValidate: true },
                            );
                          },
                        })}
                        className={`${
                          isDarkMode
                            ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                            : "bg-white border-gray-200"
                        } ${
                          errors.email
                            ? "border-red-500 focus-visible:ring-red-500"
                            : ""
                        }`}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label
                        className={`text-xs ${
                          isDarkMode ? "text-gray-300" : "text-gray-600"
                        }`}
                      >
                        Mobile
                      </Label>
                      <Input
                        type="tel"
                        value={watch("mobile") || ""}
                        inputMode="tel"
                        autoComplete="tel"
                        maxLength={20}
                        placeholder="07700 900123"
                        pattern="^[0-9+()\-\s]*$"
                        disabled={isConsultationCompleted}
                        {...register("mobile", {
                          onChange: (e) => {
                            if (isConsultationCompleted) return;
                            const raw = (e.target as HTMLInputElement).value;
                            const sanitized = raw.replace(/[^0-9+()\-\s]/g, "");
                            setValue("mobile", sanitized, {
                              shouldValidate: true,
                              shouldDirty: true,
                            });
                          },
                          onBlur: (e) => {
                            setValue(
                              "mobile",
                              (e.target as HTMLInputElement).value.trim(),
                              { shouldValidate: true },
                            );
                          },
                        })}
                        className={`${
                          isDarkMode
                            ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                            : "bg-white border-gray-200"
                        } ${
                          errors.mobile
                            ? "border-red-500 focus-visible:ring-red-500"
                            : ""
                        }`}
                      />
                    </div>
                  </div>
                </div>

                {/* Confirmation method */}
                <div className="mt-4 space-y-2">
                  <Label
                    className={`text-sm font-medium transition-colors duration-300 ${
                      isDarkMode ? "text-gray-200" : "text-gray-700"
                    }`}
                  >
                    Send booking confirmation via
                  </Label>
                  <RadioGroup
                    value={watch("confirmationMethod") as any}
                    onValueChange={(v) =>
                      setValue("confirmationMethod", v as any, {
                        shouldValidate: true,
                      })
                    }
                    className="grid grid-cols-2 gap-4"
                  >
                    <div
                      className={`flex items-center gap-2 p-3 rounded-md border ${
                        isDarkMode ? "border-gray-700" : "border-gray-200"
                      }`}
                    >
                      <RadioGroupItem
                        value="email"
                        id="confirm-email"
                        disabled={
                          !watch("email") ||
                          Boolean((errors as any).email) ||
                          isConsultationCompleted
                        }
                      />
                      <Label htmlFor="confirm-email" className="flex-1">
                        Email
                        <span
                          className={`block text-xs mt-0.5 ${
                            isDarkMode ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          {watch("email") || "No email provided"}
                        </span>
                      </Label>
                    </div>
                    <div
                      className={`flex items-center gap-2 p-3 rounded-md border ${
                        isDarkMode ? "border-gray-700" : "border-gray-200"
                      }`}
                    >
                      <RadioGroupItem
                        value="mobile"
                        id="confirm-mobile"
                        disabled={
                          !watch("mobile") ||
                          Boolean((errors as any).mobile) ||
                          isConsultationCompleted
                        }
                      />
                      <Label htmlFor="confirm-mobile" className="flex-1">
                        Mobile (SMS)
                        <span
                          className={`block text-xs mt-0.5 ${
                            isDarkMode ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          {watch("mobile") || "No mobile provided"}
                        </span>
                      </Label>
                    </div>
                  </RadioGroup>
                </div>

                {/* Notes */}
                <div className="mt-4 space-y-2">
                  <Label
                    htmlFor="notes"
                    className={`text-sm font-medium transition-colors duration-300 ${
                      isDarkMode ? "text-gray-200" : "text-gray-700"
                    }`}
                  >
                    Notes (optional)
                  </Label>
                  <Input
                    id="notes"
                    type="text"
                    placeholder="Additional notes or instructions"
                    disabled={isEventCalendarLoading || isConsultationCompleted}
                    {...register("notes")}
                    className="w-full"
                  />
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* TASKS TO COMPLETE SECTION */}
            {(localTasks?.length > 0 || !false) && (
              <AccordionItem
                value="tasks"
                className={`border-b transition-colors duration-300 ${isDarkMode ? "border-gray-600" : "border-gray-200"}`}
              >
                <AccordionTrigger
                  className={`px-0 py-4 ${isDarkMode ? "hover:bg-gray-700/50" : "hover:bg-gray-50"}`}
                >
                  <div className="flex items-center gap-2 font-semibold">
                    <CheckSquare className="w-5 h-5" />
                    <span>Tasks to Complete</span>
                    {localTasks?.length > 0 && (
                      <span className="ml-2 inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-white bg-blue-600 rounded-full">
                        {localTasks.length}
                      </span>
                    )}
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-0 pb-6 !overflow-visible">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      {localTasks?.map((task) => (
                        <div
                          key={task.id}
                          className={`flex items-center justify-between p-3 rounded-lg border ${isDarkMode ? "bg-gray-700 border-gray-600" : "bg-gray-50 border-gray-200"}`}
                        >
                          <span
                            className={`text-sm ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}
                          >
                            {task.label}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleRemoveTask(task.id)}
                            className={`p-1 rounded hover:${isDarkMode ? "bg-gray-600" : "bg-gray-200"}`}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2 pt-2">
                      <Input
                        type="text"
                        placeholder="Add new task"
                        value={taskLabel}
                        onChange={(e) => handleTaskLabelChange(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && taskLabel.trim()) {
                            e.preventDefault();
                            handleAddTask(taskLabel);
                          }
                        }}
                        className={`${isDarkMode ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400" : "bg-white border-gray-200"}`}
                      />
                      <Button
                        type="button"
                        onClick={() => handleAddTask(taskLabel)}
                        disabled={!taskLabel.trim()}
                        size="sm"
                        className="gap-1"
                      >
                        <Plus className="w-4 h-4" />
                        Add
                      </Button>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            )}

            {/* VACCINES TO ADMINISTER SECTION */}
            {(localVaccines.length > 0 || !false) && (
              <AccordionItem
                value="vaccines"
                className={`border-b transition-colors duration-300 ${isDarkMode ? "border-gray-600" : "border-gray-200"}`}
              >
                <AccordionTrigger
                  className={`px-0 py-4 ${isDarkMode ? "hover:bg-gray-700/50" : "hover:bg-gray-50"}`}
                >
                  <div className="flex items-center gap-2 font-semibold">
                    <Syringe className="w-5 h-5" />
                    <span>Vaccines to Administer</span>
                    {localVaccines.length > 0 && (
                      <span className="ml-2 inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-white bg-purple-600 rounded-full">
                        {localVaccines.length}
                      </span>
                    )}
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-0 pb-6 !overflow-visible">
                  <div className="space-y-4">
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {localVaccines.map((vaccine) => (
                        <div
                          key={vaccine.id}
                          className={`flex items-start justify-between p-3 rounded-lg border ${isDarkMode ? "bg-gray-700 border-gray-600" : "bg-gray-50 border-gray-200"}`}
                        >
                          <div className="flex-1 min-w-0">
                            <p
                              className={`text-sm font-medium ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}
                            >
                              {vaccine.name}
                            </p>
                            {vaccine.dose && (
                              <p
                                className={`text-xs mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                              >
                                {vaccine.dose}
                              </p>
                            )}
                            {vaccine.notes && (
                              <p
                                className={`text-xs mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                              >
                                {vaccine.notes}
                              </p>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveVaccine(vaccine.id)}
                            className={`p-1 rounded hover:${isDarkMode ? "bg_gray-600" : "bg-gray-200"} ml-2 flex-shrink-0`}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="space-y-2 pt-2">
                      <Input
                        type="text"
                        placeholder="Search vaccine..."
                        value={vaccineIdState}
                        onChange={(e) => handleVaccineIdChange(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && vaccineIdState.trim()) {
                            e.preventDefault();
                            handleAddVaccine(vaccineIdState);
                          }
                        }}
                        className={`${isDarkMode ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400" : "bg-white border-gray-200"}`}
                      />
                      {/* <Button
                        type="button"
                        onClick={() => {
                          if (vaccineIdState.trim()) {
                            handleAddVaccine(vaccineIdState);
                          }
                        }}
                        disabled={!vaccineIdState.trim()}
                        size="sm"
                        className="w-full gap-1"
                      >
                        <Plus className="w-4 h-4" />
                        Add Vaccine
                      </Button> */}
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            )}
          </Accordion>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="flex-1"
              disabled={isEventCalendarLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 text-white bg-slate-900 hover:bg-slate-800"
              disabled={isEventCalendarLoading || isConsultationCompleted}
            >
              {isEventCalendarLoading ? (
                <span className="inline-flex items-center gap-1">
                  {bookingDetails ? "Updating" : "Creating"}
                  <span className="">...</span>
                </span>
              ) : bookingDetails ? (
                "Update Appointment"
              ) : (
                "Create Appointment"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PatientBookingModal;
